import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Tooltip } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  BookOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  BarChartOutlined,
  SolutionOutlined,
  SafetyCertificateOutlined,
  DatabaseOutlined,
  FileWordOutlined,
  HistoryOutlined,
  FolderOutlined,
  BankOutlined,
  DollarOutlined,
  ReconciliationOutlined,
  FileSearchOutlined,
  RobotOutlined,
  DesktopOutlined,
  DashboardOutlined,
  QuestionCircleOutlined,
  MessageOutlined,
  CommentOutlined,
  NotificationOutlined,
  CarOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Sider } = Layout;

// Menu item interface
interface MenuItemType {
  key: string;
  icon: React.ReactNode;
  label: string;
  path?: string;
  moduleKey: string;
  children?: MenuItemType[];
}

// Menu items definition với phân quyền - FIXED để match database ModuleKey
const menuItems: MenuItemType[] = [
  {
    key: 'dashboard',
    icon: <AppstoreOutlined />,
    label: 'Trang chủ',
    path: '/dashboard',
    moduleKey: 'DASHBOARD_MODULE'
  },
  {
    key: 'danhmuc',
    icon: <AppstoreOutlined />,
    label: 'Danh Mục',
    moduleKey: 'CATEGORY_MODULE',
    children: [
      {
        key: 'users',
        icon: <UserOutlined />,
        label: 'Người dùng',
        path: '/users',
        moduleKey: 'USERS_MODULE'  // ✅ Fixed: USER_MANAGEMENT_MODULE → USERS_MODULE
      },
      {
        key: 'roles',
        icon: <SafetyCertificateOutlined />,
        label: 'Quản lý vai trò',
        path: '/roles',
        moduleKey: 'ROLES_MODULE'
      },
      {
        key: 'nganhhoc',
        icon: <BookOutlined />,
        label: 'Ngành học',
        path: '/nganhhoc',
        moduleKey: 'MAJOR_MODULE'  // ✅ Fixed: NGANHHOC_MODULE → MAJOR_MODULE
      },
      {
        key: 'courses',
        icon: <BookOutlined />,
        label: 'Khóa học',
        path: '/courses',
        moduleKey: 'COURSES_MODULE'  // ✅ Fixed: COURSE_MODULE → COURSES_MODULE
      },
      {
        key: 'subjects',
        icon: <BookOutlined />,
        label: 'Môn học',
        path: '/subjects',
        moduleKey: 'SUBJECTS_MODULE'  // ✅ Fixed: SUBJECT_MANAGEMENT_MODULE → SUBJECTS_MODULE
      },
      {
        key: 'classes',
        icon: <TeamOutlined />,
        label: 'Lớp học',
        path: '/classes',
        moduleKey: 'CLASSES_MODULE'  // ✅ Fixed: CLASS_MANAGEMENT_MODULE → CLASSES_MODULE
      },
      {
        key: 'lop-hoc-phan',
        icon: <BookOutlined />,
        label: 'Lớp học phần',
        path: '/lop-hoc-phan',
        moduleKey: 'CLASS_SECTIONS_MODULE'  // ✅ Fixed: LOP_HOC_PHAN_MODULE → CLASS_SECTIONS_MODULE
      },
      {
        key: 'phieu-cham',
        icon: <FileTextOutlined />,
        label: 'Phiếu chấm',
        path: '/phieu-cham',
        moduleKey: 'SCORING_FORM_MODULE'  // ✅ Fixed: PHIEU_CHAM_MODULE → SCORING_FORM_MODULE
      },
      {
        key: 'nhom-thuc-hanh',
        icon: <TeamOutlined />,
        label: 'Nhóm thực hành',
        path: '/nhom-thuc-hanh',
        moduleKey: 'PRACTICE_GROUP_MODULE'  // ✅ Fixed: NHOM_THUC_HANH_MODULE → PRACTICE_GROUP_MODULE
      },
      {
        key: 'sinh-vien',
        icon: <SolutionOutlined />,
        label: 'Sinh viên',
        path: '/sinh-vien',
        moduleKey: 'STUDENTS_MODULE'  // ✅ Fixed: STUDENT_MANAGEMENT_MODULE → STUDENTS_MODULE
      },
      {
        key: 'rooms',
        icon: <BankOutlined />,
        label: 'Phòng học',
        path: '/rooms',
        moduleKey: 'ROOMS_MODULE'
      },
      {
        key: 'data-sync',
        icon: <DatabaseOutlined />,
        label: 'Đồng bộ dữ liệu',
        path: '/data-sync',
        moduleKey: 'COURSES_MODULE'  // Use COURSES_MODULE for permission check
      },
      {
        key: 'grade-sync',
        icon: <DatabaseOutlined />,
        label: 'Đồng bộ điểm',
        path: '/grade-sync',
        moduleKey: 'GRADES_MODULE'  // Use GRADES_MODULE for permission check
      }
    ]
  },
  {
    key: 'diemdanh',
    icon: <SolutionOutlined />,
    label: 'Điểm danh',
    moduleKey: 'ATTENDANCE_MODULE',
    children: [
      {
        key: 'diemdanh-lythuyet',
        icon: <FileTextOutlined />,
        label: 'Điểm danh lý thuyết',
        path: '/attendance/theory',
        moduleKey: 'THEORETICAL_ATTENDANCE_MODULE'  // ✅ Fixed: ATTENDANCE_THEORY_MODULE → THEORETICAL_ATTENDANCE_MODULE
      },
      {
        key: 'diemdanh-thuchanh',
        icon: <FileTextOutlined />,
        label: 'Điểm danh thực hành',
        path: '/attendance/practice',
        moduleKey: 'PRACTICAL_ATTENDANCE_MODULE'  // ✅ Fixed: ATTENDANCE_PRACTICE_MODULE → PRACTICAL_ATTENDANCE_MODULE
      },
      {
        key: 'lich-giang-tuan',
        icon: <FileTextOutlined />,
        label: 'Lịch giảng tuần',
        path: '/teacher-weekly-schedule',
        moduleKey: 'TEACHER_WEEKLY_SCHEDULE_MODULE'
      },
      {
        key: 'diemdanh-canbo',
        icon: <TeamOutlined />,
        label: 'Điểm danh cán bộ',
        path: '/daily-attendance/management',
        moduleKey: 'DAILY_ATTENDANCE_MODULE'
      },
      {
        key: 'diemdanh-tv-school',
        icon: <DesktopOutlined />,
        label: 'Màn hình TV - Trường',
        path: '/daily-attendance/tv/school',
        moduleKey: 'DAILY_ATTENDANCE_MODULE'
      },
      {
        key: 'diemdanh-tv-hospital',
        icon: <DesktopOutlined />,
        label: 'Màn hình TV - Bệnh viện',
        path: '/daily-attendance/tv/hospital',
        moduleKey: 'DAILY_ATTENDANCE_MODULE'
      }
    ]
  },
  {
    key: 'diemdanh-cuochop',
    icon: <TeamOutlined />,
    label: 'Điểm danh cuộc họp',
    moduleKey: 'MEETING_ATTENDANCE_MODULE',
    children: [
      {
        key: 'quanly-cuochop',
        icon: <TeamOutlined />,
        label: 'Quản lý cuộc họp',
        path: '/meeting-attendance',
        moduleKey: 'MEETING_MANAGEMENT_MODULE'
      },
      {
        key: 'thongke-cuochop',
        icon: <BarChartOutlined />,
        label: 'Thống kê cuộc họp',
        path: '/meeting-statistics',
        moduleKey: 'MEETING_STATISTICS_MODULE'
      }
    ]
  },
  {
    key: 'ky-thi',
    icon: <FileTextOutlined />,
    label: 'Kỳ thi/Phiên chấm',
    path: '/exam-sessions',
    moduleKey: 'EXAM_SESSION_MODULE'
  },
  {
    key: 'parking',
    icon: <CarOutlined />,
    label: 'Quản lý gửi xe',
    moduleKey: 'VEHICLE_MODULE',
    children: [
      {
        key: 'parking-management',
        icon: <SolutionOutlined />,
        label: 'Đăng ký thẻ xe',
        path: '/parking/management',
        moduleKey: 'VEHICLE_MODULE'
      },
      {
        key: 'parking-logs',
        icon: <HistoryOutlined />,
        label: 'Nhật ký vào ra',
        path: '/parking/logs',
        moduleKey: 'VEHICLE_MODULE'
      },
      {
        key: 'parking-stats',
        icon: <BarChartOutlined />,
        label: 'Thống kê gửi xe',
        path: '/parking/stats',
        moduleKey: 'VEHICLE_MODULE'
      }
    ]
  },
  {
    key: 'exam-report',
    icon: <FileWordOutlined />,
    label: 'Báo cáo điểm kỳ thi',
    path: '/exam-score-report',
    moduleKey: 'EXAM_REPORT_MODULE'
  },
  {
    key: 'khaothi',
    icon: <FileSearchOutlined />,
    label: 'Khảo thí',
    moduleKey: 'EXAM_DEPARTMENT_MODULE',
    children: [
      {
        key: 'question-bank',
        icon: <BankOutlined />,
        label: 'Ngân hàng câu hỏi',
        path: '/exam/question-bank',
        moduleKey: 'EXAM_QUESTION_BANK_MODULE'
      },
      {
        key: 'exam-paper',
        icon: <FileTextOutlined />,
        label: 'Tạo đề thi tự luận',
        path: '/exam/generate-exam',
        moduleKey: 'EXAM_GENERATE_MODULE'  // ✅ Fixed: EXAM_PAPER_CREATION_MODULE → EXAM_GENERATE_MODULE
      },
      {
        key: 'exam-history',
        icon: <HistoryOutlined />,
        label: 'Lịch sử đề thi',
        path: '/exam/history',
        moduleKey: 'EXAM_HISTORY_MODULE'
      },
      {
        key: 'exam-statistics',
        icon: <BarChartOutlined />,
        label: 'Thống kê khảo thí',
        path: '/exam/statistics',
        moduleKey: 'EXAM_STATISTICS_MODULE'
      }
    ]
  },
  {
    key: 'hocbu',
    icon: <ReconciliationOutlined />,
    label: 'Quản lý học bù',
    moduleKey: 'MAKEUP_MODULE',
    children: [
      {
        key: 'hocbu-daotao',
        icon: <BookOutlined />,
        label: 'Học bù - Đào tạo',
        path: '/makeup/training',
        moduleKey: 'STUDENT_MAKEUP_MODULE'  // ✅ Fixed: MAKEUP_MODULE → STUDENT_MAKEUP_MODULE
      },
      {
        key: 'hocbu-ketoan',
        icon: <DollarOutlined />,
        label: 'Học bù - Kế toán',
        path: '/makeup/accounting',
        moduleKey: 'ACCOUNTING_MAKEUP_MODULE'  // ✅ Fixed: ACCOUNTING_MODULE → ACCOUNTING_MAKEUP_MODULE
      }
    ]
  },
  {
    key: 'tuition',
    icon: <DollarOutlined />,
    label: 'Quản lý học phí',
    path: '/tuition',
    moduleKey: 'TUITION_MODULE'
  },
  {
    key: 'grades',
    icon: <BarChartOutlined />,
    label: 'Quản lý điểm',
    moduleKey: 'GRADE_MANAGEMENT_MODULE',
    children: [
      {
        key: 'grade-input',
        icon: <FileTextOutlined />,
        label: 'Nhập điểm',
        path: '/grades/input',
        moduleKey: 'GRADE_INPUT_MODULE'
      },
      {
        key: 'grade-report',
        icon: <BarChartOutlined />,
        label: 'Báo cáo điểm',
        path: '/grades/report',
        moduleKey: 'GRADE_MANAGEMENT_MODULE'  // ✅ Using parent module since GRADE_REPORT_MODULE doesn't exist in DB
      }
    ]
  },
  {
    key: 'statistic',
    icon: <BarChartOutlined />,
    label: 'Thống kê',
    moduleKey: 'STATISTICS_MODULE',  // ✅ Fixed: STATISTIC_MODULE → STATISTICS_MODULE
    children: [
      {
        key: 'stat-absence',
        icon: <FileTextOutlined />,
        label: 'SV vắng/muộn theo môn học',
        path: '/statistic/student-absence',
        moduleKey: 'STUDENT_ABSENCE_MODULE'  // ✅ Fixed: STAT_ABSENCE_MODULE → STUDENT_ABSENCE_MODULE
      },
      {
        key: 'stat-giogiang',
        icon: <FileTextOutlined />,
        label: 'Giờ giảng theo môn học',
        path: '/statistic/teaching-hours',
        moduleKey: 'TEACHING_HOURS_MODULE'  // ✅ Fixed: STAT_TEACHING_HOURS_MODULE → TEACHING_HOURS_MODULE
      },
      {
        key: 'stat-giogiang-teacher',
        icon: <FileTextOutlined />,
        label: 'Giờ giảng theo giáo viên',
        path: '/statistic/teaching-hours-by-teacher',
        moduleKey: 'TEACHING_HOURS_BY_TEACHER_MODULE'  // ✅ Fixed: STAT_TEACHING_HOURS_BY_TEACHER_MODULE → TEACHING_HOURS_BY_TEACHER_MODULE
      }
      ,
      {
        key: 'teacher-extra-hours',
        icon: <FileTextOutlined />,
        label: 'Giờ coi/chấm/LS (GV)',
        path: '/teacher/extra-hours',
        moduleKey: 'TEACHER_EXTRA_HOURS_MODULE'
      },
      {
        key: 'stat-attendance-start',
        icon: <FileTextOutlined />,
        label: 'Điểm danh đầu giờ',
        path: '/statistic/attendance-start',
        moduleKey: 'STATISTICS_MODULE'
      },
      {
        key: 'daily-attendance-stats',
        icon: <FileTextOutlined />,
        label: 'Thống kê điểm danh cán bộ hàng ngày',
        path: '/statistic/daily-attendance',
        moduleKey: 'DAILY_ATTENDANCE_STATISTICS_MODULE'
      }
    ]
  },
  {
    key: 'permissions',
    icon: <SafetyCertificateOutlined />,
    label: 'Phân quyền',
    moduleKey: 'ROLE_PERMISSIONS_MODULE',
    children: [
      {
        key: 'role-permissions',
        icon: <SafetyCertificateOutlined />,
        label: 'Phân quyền vai trò',
        path: '/permissions/roles',
        moduleKey: 'ROLE_PERMISSIONS_MODULE'
      },
      {
        key: 'user-permissions',
        icon: <UserOutlined />,
        label: 'Phân quyền người dùng',
        path: '/permissions/users',
        moduleKey: 'ROLE_PERMISSIONS_MODULE'
      }
    ]
  },
  {
    key: 'profile',
    icon: <UserOutlined />,
    label: 'Hồ sơ cá nhân',
    path: '/profile',
    moduleKey: 'PROFILE_MODULE'
  },
  {
    key: 'online-training',
    icon: <AppstoreOutlined />,
    label: 'Quản lý đào tạo trực tuyến',
    moduleKey: 'ONLINE_TRAINING_MODULE',
    children: [
      {
        key: 'moodle-users',
        icon: <UserOutlined />,
        label: 'Người dùng Moodle',
        path: '/moodle/users',
        moduleKey: 'MOODLE_USERS_MODULE'
      },
      {
        key: 'moodle-categories',
        icon: <FolderOutlined />,
        label: 'Danh mục Moodle',
        path: '/moodle/categories',
        moduleKey: 'MOODLE_CATEGORIES_MODULE'
      },
      {
        key: 'moodle-courses',
        icon: <BookOutlined />,
        label: 'Khóa học Moodle',
        path: '/moodle/courses',
        moduleKey: 'MOODLE_COURSES_MODULE'
      }
    ]
  },
  {
    key: 'co-van-hoc-tap-ao',
    icon: <RobotOutlined />,
    label: 'Cố vấn học tập ảo',
    moduleKey: 'VIRTUAL_ADVISOR_MODULE',
    children: [
      {
        key: 'virtual-advisor-dashboard',
        icon: <DashboardOutlined />,
        label: 'Tổng quan',
        path: '/admin/virtual-advisor',
        moduleKey: 'VIRTUAL_ADVISOR_MODULE'
      },
      {
        key: 'virtual-advisor-qa-pairs',
        icon: <QuestionCircleOutlined />,
        label: 'Quản lý Q&A',
        path: '/admin/virtual-advisor/qa-pairs',
        moduleKey: 'VIRTUAL_ADVISOR_MODULE'
      },
      {
        key: 'ai-chat-statistics',
        icon: <BarChartOutlined />,
        label: 'Thống kê Chat AI',
        path: '/admin/ai-chat-statistics',
        moduleKey: 'AI_CHAT_STATISTICS_MODULE'
      }
    ]
  },
  {
    key: 'ai-document-assistant',
    icon: <MessageOutlined />,
    label: 'Cố vấn AI (File Search)',
    moduleKey: 'AI_ASSISTANT_MODULE',
    children: [
      {
        key: 'ai-assistant-collections',
        icon: <FolderOutlined />,
        label: 'Bộ sưu tập',
        path: '/admin/ai-assistant',
        moduleKey: 'AI_ASSISTANT_MODULE'
      },
      {
        key: 'ai-assistant-documents',
        icon: <FileTextOutlined />,
        label: 'Tài liệu',
        path: '/admin/ai-assistant',
        moduleKey: 'AI_ASSISTANT_MODULE'
      },
      {
        key: 'ai-assistant-analytics',
        icon: <BarChartOutlined />,
        label: 'Phân tích',
        path: '/admin/ai-assistant',
        moduleKey: 'AI_ASSISTANT_MODULE'
      }
    ]
  },
  {
    key: 'support',
    icon: <MessageOutlined />,
    label: 'Hỗ trợ & Thông báo',
    moduleKey: 'FEEDBACK_MODULE',
    children: [
      {
        key: 'notification-management',
        icon: <NotificationOutlined />,
        label: 'Quản lý Thông báo',
        path: '/admin/notifications',
        moduleKey: 'NOTIFICATION_MODULE'
      },
      {
        key: 'feedback-management',
        icon: <CommentOutlined />,
        label: 'Quản lý Phản hồi',
        path: '/admin/feedback',
        moduleKey: 'FEEDBACK_MODULE'
      }
    ]
  }
];

// Utility: filter menu items by permissions
function filterMenuByPermissions(
  menuItems: MenuItemType[],
  permissions: any[],
  userInfo: any
): MenuItemType[] {
  console.log('🔍 === SIDEBAR PERMISSION FILTER START ===');
  console.log('UserInfo:', userInfo);
  console.log('Permissions:', permissions);

  // Check for admin role (case insensitive) - CHÍNH XÁC THEO DATABASE
  const roleKey = (userInfo?.RoleKey || userInfo?.roleKey || '').toLowerCase();
  const roleName = (userInfo?.RoleName || userInfo?.roleName || '').toLowerCase();
  const roleId = userInfo?.RoleId || userInfo?.roleId;
  const username = (userInfo?.Username || userInfo?.username || '').toLowerCase();

  const isAdmin = roleKey === 'admin' ||
    roleName === 'admin' ||
    roleId === 1 ||
    username === 'admin';

  console.log('� Role checks:', {
    roleKey,
    roleName,
    roleId,
    username,
    isAdmin
  });

  // QUAN TRỌNG: CHỈ admin mới được full access
  if (isAdmin) {
    console.log('✅ User is admin - returning all menu items');
    console.log('🔍 === SIDEBAR PERMISSION FILTER END - ADMIN ===');
    return menuItems;
  }

  // Normalize module keys cho user thường
  const allowedModules = new Set<string>();

  // Lấy user permissions riêng (quyền cá nhân)
  const oldUserPermissions = JSON.parse(localStorage.getItem('userPermissions') || '[]');
  const newUserPermissions = JSON.parse(localStorage.getItem('quanly_truonghoc_user_permissions') || '[]');
  const userPermissions = newUserPermissions.length > 0 ? newUserPermissions : oldUserPermissions;

  // ✅ LOG: Hiển thị rõ giá trị quyền cá nhân và tổng hợp
  console.log('🟦 [Sidebar] userPermissions:', userPermissions);
  console.log('� [Sidebar] rolePermissions:', permissions);
  const allPermissions = [...(permissions || []), ...(userPermissions || [])];
  console.log('🟨 [Sidebar] allPermissions:', allPermissions);

  // Debugging: Log tất cả module keys từ permissions
  console.log('🔍 All module keys from permissions:');
  allPermissions.forEach((p, index) => {
    const moduleKey = p.ModuleKey || p.modulekey || p.Module || p.module;
    const hasPermission = p.CanView || p.canView || p.View || p.view ||
      p.CanAll || p.canAll || p.All || p.all;
    console.log(`   ${index + 1}. ${moduleKey} - HasView: ${hasPermission}`);
  });

  allPermissions.forEach(p => {
    const moduleKey = p.ModuleKey || p.modulekey || p.Module || p.module || '';
    const key = moduleKey.toLowerCase();
    // Kiểm tra các quyền có thể có
    const hasPermission = p.CanView || p.canView || p.View || p.view ||
      p.CanAll || p.canAll || p.All || p.all ||
      p.CanRead || p.canRead || p.Read || p.read;
    console.log('🔎 [Sidebar] Check permission:', { raw: moduleKey, key, hasPermission });
    if (key && hasPermission) {
      allowedModules.add(key);
      console.log(`✅ Added permission: ${key} (${hasPermission})`);
    }
  });

  // Luôn cho phép Profile và Dashboard cho mọi user đã đăng nhập
  allowedModules.add('profile_module');
  allowedModules.add('dashboard_module');

  console.log('📝 Final allowed modules:', Array.from(allowedModules));

  // Kiểm tra xem một menu item có quyền hiển thị không
  function hasMenuPermission(item: MenuItemType): boolean {
    // Luôn cho phép profile và dashboard
    if (item.key === 'profile' || item.key === 'dashboard') {
      console.log(`✅ ${item.key}: Always allowed`);
      return true;
    }

    // Kiểm tra moduleKey của item
    if (item.moduleKey) {
      const itemModuleKey = item.moduleKey.toLowerCase();
      const hasAccess = allowedModules.has(itemModuleKey);
      console.log(`🔍 Checking ${item.key} (${item.moduleKey} → ${itemModuleKey}): ${hasAccess ? '✅ ALLOWED' : '❌ DENIED'}`);
      console.log('🔑 allowedModules:', Array.from(allowedModules));
      return hasAccess;
    }

    console.log(`⚠️ ${item.key}: No moduleKey defined`);
    return false;
  }

  // Lọc menu items dựa trên quyền
  function filterItems(items: MenuItemType[]): MenuItemType[] {
    const result: MenuItemType[] = [];

    for (const item of items) {
      if (item.children && item.children.length > 0) {
        // Lọc menu con
        const filteredChildren = filterItems(item.children);

        if (filteredChildren.length > 0) {
          // Nếu có ít nhất một menu con được phân quyền, thêm menu cha với các menu con đã lọc
          result.push({
            ...item,
            children: filteredChildren
          });
        }
      } else if (hasMenuPermission(item)) {
        // Menu không có con và có quyền - thêm vào kết quả
        result.push(item);
      }
    }

    return result;
  }

  return filterItems(menuItems);
}

// Helper: render menu items with Tooltip for long label
function renderMenuItems(items: MenuItemType[], collapsed: boolean): MenuProps['items'] {
  return items.map(item => {
    if (item.children) {
      return {
        key: item.key,
        icon: item.icon,
        label: (
          <Tooltip placement="right" title={collapsed ? item.label : ''}>
            <span style={{
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block',
              verticalAlign: 'middle'
            }}>
              {item.label}
            </span>
          </Tooltip>
        ),
        children: renderMenuItems(item.children, collapsed)
      };
    }
    return {
      key: item.key,
      icon: item.icon,
      label: (
        <Tooltip placement="right" title={collapsed ? item.label : ''}>
          <span style={{
            maxWidth: 160,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block',
            verticalAlign: 'middle'
          }}>
            {item.label}
          </span>
        </Tooltip>
      )
    };
  });
}

// Helper: xác định menu cha cấp 1 của một key
function getRootMenuKey(key: string, items: MenuItemType[] = menuItems): string | null {
  for (const item of items) {
    if (item.key === key) return item.key;
    if (item.children) {
      const found = getRootMenuKey(key, item.children);
      if (found) return item.key;
    }
  }
  return null;
}

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Lấy thông tin user và quyền từ localStorage
  const userInfo = useMemo(() => {
    try {
      // Try TypeScript system first, fallback to old system
      const tsUser = localStorage.getItem('quanly_truonghoc_user');
      const oldUser = localStorage.getItem('userInfo');
      return JSON.parse(tsUser || oldUser || '{}');
    } catch {
      return {};
    }
  }, []);

  const permissions = useMemo(() => {
    try {
      // ✅ FIX: Read role permissions from localStorage directly (NOT from userInfo.permissions)
      const tsPermissions = localStorage.getItem('quanly_truonghoc_permissions');
      const oldPermissions = localStorage.getItem('permissions');
      const perms = JSON.parse(tsPermissions || oldPermissions || '[]');
      console.log('🔍 [Sidebar] Role Permissions loaded:', perms);
      return perms;
    } catch (error) {
      console.error('❌ [Sidebar] Error loading permissions:', error);
      return [];
    }
  }, []);

  // Lọc menu theo quyền với useMemo để tránh infinite loop
  const filteredMenuItems = useMemo(() => {
    // Debug log only when dependencies change
    console.log('🔍 Sidebar Debug - User Role:', userInfo?.roleKey || userInfo?.RoleKey, 'UserInfo:', userInfo);
    console.log('🔍 Sidebar Debug - Permissions:', permissions);
    const filtered = filterMenuByPermissions(menuItems, permissions, userInfo);
    console.log('🟢 [Sidebar] Filtered menu items:', filtered);
    return filtered;
  }, [permissions, userInfo]);

  // Tìm path theo key
  const findPathByKey = (key: string, items: MenuItemType[] = filteredMenuItems): string | null => {
    for (const item of items) {
      if (item.key === key && item.path) return item.path;
      if (item.children) {
        const found = findPathByKey(key, item.children);
        if (found) return found;
      }
    }
    return null;
  };

  // Xác định selected key dựa trên pathname với useMemo
  const selectedKey = useMemo(() => {
    const pathname = location.pathname;

    // Map specific paths to menu keys
    const pathToKeyMap: Record<string, string> = {
      '/dashboard': 'dashboard',
      '/users': 'users',
      '/roles': 'roles',
      '/nganhhoc': 'nganhhoc',
      '/courses': 'courses',
      '/subjects': 'subjects',
      '/classes': 'classes',
      '/students': 'students',
      '/attendance/theory': 'diemdanh-lythuyet',
      '/attendance/practice': 'diemdanh-thuchanh',
      '/teacher-weekly-schedule': 'lich-giang-tuan',
      '/meeting-attendance': 'quanly-cuochop',
      '/meeting-statistics': 'thongke-cuochop',
      '/statistic/attendance-start': 'stat-attendance-start',
      '/admin/virtual-advisor': 'virtual-advisor-dashboard',
      '/admin/virtual-advisor/qa-pairs': 'virtual-advisor-qa-pairs',
      '/admin/ai-chat-statistics': 'ai-chat-statistics',
      '/admin/feedback': 'feedback-management',
      '/admin/notifications': 'notification-management',
      '/admin/ai-assistant': 'ai-assistant-collections',
      '/grades/input': 'grade-input',
      '/grades/report': 'grade-report',
      '/permissions/roles': 'role-permissions',
      '/permissions/users': 'user-permissions',
      '/profile': 'profile',
      '/debug/permissions': 'debug-permissions',
      '/debug/login': 'debug-login',
      '/makeup/training': 'hocbu-daotao',
      '/makeup/accounting': 'hocbu-ketoan',
      '/tuition': 'tuition',
      '/statistic/teaching-hours': 'teaching-hours',
      '/statistic/teaching-hours-by-teacher': 'teaching-hours-by-teacher'
      ,
      '/teacher/extra-hours': 'teacher-extra-hours',
      '/parking/management': 'parking-management',
      '/parking/logs': 'parking-logs',
      '/parking/stats': 'parking-stats',
      '/rooms': 'rooms'
    };

    return pathToKeyMap[pathname] || 'dashboard';
  }, [location.pathname]);

  // Auto expand parent menu when page loads
  useEffect(() => {
    const rootKey = getRootMenuKey(selectedKey, filteredMenuItems);
    if (rootKey && rootKey !== selectedKey && !collapsed) {
      setOpenKeys([rootKey]);
    }
  }, [selectedKey, collapsed, filteredMenuItems]);

  const handleMenuClick = ({ key }: { key: string }) => {
    const path = findPathByKey(key);
    if (path) {
      navigate(path);
    }
  };

  const handleOpenChange = (keys: string[]) => {
    if (!collapsed) {
      setOpenKeys(keys);
    }
  };

  return (
    <Sider
      collapsed={collapsed}
      width={240}
      style={{
        overflow: 'hidden',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 999
      }}
      theme="dark"
    >
      <div style={{
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255, 255, 255, 0.1)',
        margin: '16px',
        borderRadius: '6px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <img
            src="/logoCDYTB.png"
            alt="Logo Trường"
            style={{
              width: collapsed ? '32px' : '40px',
              height: collapsed ? '32px' : '40px',
              marginRight: collapsed ? '0' : '8px',
              borderRadius: '4px'
            }}
          />
          {!collapsed && (
            <span style={{
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold'
            }}>
              Quản lý Trường học
            </span>
          )}
        </div>
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={collapsed ? [] : openKeys}
        onOpenChange={handleOpenChange}
        onClick={handleMenuClick}
        items={renderMenuItems(filteredMenuItems, collapsed)}
        style={{
          borderRight: 0,
          textAlign: 'left',
          height: 'calc(100vh - 96px)',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      />
    </Sider>
  );
};

export default Sidebar;